import { useParams } from "react-router-dom";
import TermsViewer, { ROLE_LABELS } from "@/components/TermsViewer";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Download, ShieldCheck } from "lucide-react";

const SLUG_TO_ROLE = {
  buyer: "buyer",
  seller: "seller",
  "cam-vendor": "cam_vendor",
  privacy: "privacy",
};

/** Public routes /terms/buyer, /terms/seller, /terms/cam-vendor, /privacy.
 *  Renders the current published version — indexed for search engines. */
export default function TermsPage({ fixedRole }) {
  const { slug } = useParams();
  const role = fixedRole || SLUG_TO_ROLE[slug] || "buyer";
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    api.get(`/terms/${role}/current`).then((r) => setDoc(r.data)).catch(() => {});
  }, [role]);

  const download = () => {
    if (!doc) return;
    const blob = new Blob([doc.body_md], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `jeweltix-${role}-v${doc.version}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid={`terms-page-${role}`}>
      <div className="flex items-start justify-between gap-4 mb-6 pb-6 border-b border-border">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Legal · Immutable versioned</span>
          </div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl">{ROLE_LABELS[role]}</h1>
          {doc && (
            <p className="text-xs font-mono text-muted-foreground mt-2">
              Version {doc.version} · Effective {doc.effective_at?.slice(0, 10)} · Hash <span className="text-[10px]">{doc.body_hash?.slice(0, 12)}…</span>
            </p>
          )}
        </div>
        <button
          onClick={download}
          disabled={!doc}
          data-testid="terms-download"
          className="text-xs border border-border rounded-md px-3 py-2 hover:bg-secondary flex items-center gap-1.5 disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" /> Download
        </button>
      </div>

      <TermsViewer role={role} expanded />
    </div>
  );
}
